import React from 'react';
import PropTypes from 'prop-types';

import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';

import { Form, TranslateDropdown } from '@weezevent/nacre';

import Style from './MultiLanguageField.module.css';

const DEFAULT_LANGUAGE = 'en-gb';

/**
 * MultiLanguageField component
 * @param {Object} props
 * @param {string} props.name - The name of the field
 * @param {string} props.value - The value of the field (for non-translatable fields)
 * @param {string} props.label - The label of the field
 * @param {Array} props.errors - The list of errors for the field
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.isTranslatable - Whether the field is translatable
 * @param {string} props.defaultLanguage - The default language code
 * @param {Array} props.languages - The list of available languages
 * @param {Object} props.translations - The translations object { lang: value }
 * @param {Function} props.onChange - The onChange handler
 * @param {Function} props.renderInput - The function to render the input field
 * @returns {JSX.Element|null} The MultiLanguageField component
 */
function MultiLanguageField({
    name,
    value = '',
    label,
    errors = [],
    required = false,
    isTranslatable = true,
    defaultLanguage = DEFAULT_LANGUAGE,
    languages = [],
    translations = {},
    onChange = noop,
    renderInput = null
}) {
    const [language, setLanguage] = React.useState(defaultLanguage);

    const handleFieldChange = event => {
        // Just a proxy for debbugging purposes
        onChange(event);
    };

    const handleLanguageChange = newValue => {
        setLanguage(newValue);
    };

    if (!isFunction(renderInput)) {
        console.warn('MultiLanguageField: renderInput is not a function');
        return null;
    }

    const hasErrors = errors.length > 0;

    const inputProps = {
        name,
        required,
        lang: language,
        errors: hasErrors,
        value: isTranslatable ? translations[language] || '' : value || '',
        onChange: handleFieldChange
    };

    return (
        <Form.Field>
            <div className={Style.multiLanguageField__header}>
                <Form.Label required={required}>{label}</Form.Label>
                {isTranslatable ? (
                    <TranslateDropdown
                        defaultValue={defaultLanguage}
                        languages={languages}
                        onChange={handleLanguageChange}
                        selected={Object.keys(translations).filter(lang => translations[lang])}
                    />
                ) : null}
            </div>
            {renderInput(inputProps)}
            {hasErrors ? <Form.FieldErrors errors={errors} /> : null}
        </Form.Field>
    );
}

MultiLanguageField._defaultLanguage = DEFAULT_LANGUAGE;
MultiLanguageField.displayName = 'MultiLanguageField';
MultiLanguageField.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    label: PropTypes.string.isRequired,
    errors: PropTypes.array,
    required: PropTypes.bool,
    isTranslatable: PropTypes.bool,
    defaultLanguage: PropTypes.string,
    languages: PropTypes.array,
    translations: PropTypes.object,
    onChange: PropTypes.func,
    renderInput: PropTypes.func.isRequired
};
export default MultiLanguageField;
